// Utilitários genéricos de exportação/importação de configurações em JSON.
//
// Usados para mover Chatflows, Funis/Estágios e Automações entre clientes.
// A exportação envolve os dados num "envelope" com `kind` para que a
// importação valide que o arquivo é do tipo esperado (impede, por ex.,
// importar uma automação numa tela de chatflow).

/**
 * Dispara o download de um objeto como arquivo .json.
 * @param {string} filename - nome do arquivo (ex: "meu-fluxo.chatflow.json")
 * @param {object} obj - objeto a serializar
 */
export function downloadJSON(filename, obj) {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Lê um arquivo selecionado e faz o parse do JSON.
 * @param {File} file
 * @returns {Promise<object>} conteúdo parseado
 * @throws {Error} mensagem amigável se o arquivo não for um JSON válido
 */
export async function readJSONFile(file) {
    let text;
    try {
        text = await file.text();
    } catch (e) {
        throw new Error('Não foi possível ler o arquivo selecionado.');
    }
    try {
        return JSON.parse(text);
    } catch (e) {
        throw new Error('O arquivo não é um JSON válido.');
    }
}

/**
 * Monta o envelope padrão de exportação.
 * @param {'lais-chatflow'|'lais-funil'|'lais-automacao'} kind
 * @param {*} data - payload já sanitizado (sem IDs de cliente)
 */
export function buildEnvelope(kind, data) {
    return {
        kind,
        version: 1,
        exportedAt: new Date().toISOString(),
        data,
    };
}

/**
 * Valida o envelope importado e devolve o `data`.
 * @param {object} envelope - objeto parseado do arquivo
 * @param {string} expectedKind - kind esperado pela tela atual
 * @returns {*} envelope.data
 * @throws {Error} se o tipo do arquivo não corresponder
 */
export function readEnvelope(envelope, expectedKind) {
    if (!envelope || typeof envelope !== 'object') {
        throw new Error('Arquivo de importação inválido.');
    }
    if (envelope.kind !== expectedKind) {
        throw new Error(
            `Este arquivo não é do tipo esperado. Selecione um arquivo de exportação válido.`
        );
    }
    if (envelope.data === undefined || envelope.data === null) {
        throw new Error('Arquivo de importação sem dados.');
    }
    return envelope.data;
}

/**
 * Zera (define como undefined) uma lista de chaves em um objeto, em qualquer
 * profundidade. Usado pelos sanitizers para remover IDs vinculados a um cliente
 * antes de exportar. Não muta o objeto original — retorna uma cópia profunda.
 * @param {*} value - objeto/array/valor a limpar
 * @param {string[]} fieldNames - chaves a zerar onde quer que apareçam
 * @returns {*} cópia sem os campos informados
 */
export function stripFields(value, fieldNames) {
    const fields = new Set(fieldNames);
    const walk = (node) => {
        if (Array.isArray(node)) {
            return node.map(walk);
        }
        if (node && typeof node === 'object') {
            const out = {};
            for (const [key, val] of Object.entries(node)) {
                if (fields.has(key)) continue; // remove o campo do cliente
                out[key] = walk(val);
            }
            return out;
        }
        return node;
    };
    return walk(value);
}
